import React, { useState, useRef, useEffect } from 'react';

const PuttyButtonUltimate = ({ server, client }) => {
  const [copied, setCopied] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('ready'); // ready, connecting, connected, failed
  const [options, setOptions] = useState({
    username: server.sshUsername || 'root',
    port: server.sshPort || '22'
  });
  const panelRef = useRef(null);
  const hiddenLinkRef = useRef(null);

  // Mettre à jour les options si les données du serveur changent
  useEffect(() => {
    setOptions({
      username: server.sshUsername || 'root',
      port: server.sshPort || '22'
    });
  }, [server.sshUsername, server.sshPort]);

  // Fermer le panel si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showOptions]);

  // Ne pas afficher le bouton si SSH n'est pas accessible
  if (!server.sshAccessible) {
    return null;
  }

  // Générer la commande SSH pour Windows Terminal
  const generateWindowsTerminalCommand = () => {
    const serverIP = server.name;
    const username = options.username || server.sshUsername || 'root';
    const port = options.port !== '22' ? ` -p ${options.port}` : '';
    
    return `wt ssh ${username}@${serverIP}${port}`;
  };

  // Générer la commande SSH standard
  const generateSSHCommand = () => {
    const serverIP = server.name;
    const username = options.username || server.sshUsername || 'root';
    const port = options.port !== '22' ? ` -p ${options.port}` : '';
    
    return `ssh ${username}@${serverIP}${port}`;
  };

  // Générer l'URL protocol handler PuTTY
  const generatePuttyUrl = () => {
    const serverIP = server.name;
    const port = options.port !== '22' ? `:${options.port}` : '';
    return `putty://${serverIP}${port}`;
  };

  // Créer et lancer directement via Node.js (backend call)
  const launchSSHViaBackend = async () => {
    const serverIP = server.name;
    const username = options.username || server.sshUsername || 'root';
    const port = options.port || '22';

    try {
      const response = await fetch('/api/launch-ssh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host: serverIP,
          username: username,
          port: port
        })
      });

      if (response.ok) {
        setConnectionStatus('connected');
        setTimeout(() => setConnectionStatus('ready'), 3000);
        return true;
      }
    } catch (err) {
      console.log('Backend SSH launch failed:', err);
    }
    return false;
  };

  // Copier dans le presse-papier avec feedback amélioré
  const copyToClipboard = async (text, label = 'Commande') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      // Fallback avec prompt
      prompt(`${label} (Ctrl+C pour copier):`, text);
      return false;
    }
  };

  // MÉTHODE ULTIMATE CLIENT/SERVEUR - Le serveur génère, le client exécute !
  const launchSSHUltimate = async () => {
    const serverIP = server.name;
    const username = options.username || server.sshUsername || 'root';
    const port = options.port || '22';
    
    setConnectionStatus('connecting');

    try {
      // NOUVELLE APPROCHE: Demander les commandes au serveur
      const response = await fetch('/api/launch-ssh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('bastionToken') ? 
            `Bearer ${localStorage.getItem('bastionToken')}` : 
            'Bearer dev-token'
        },
        body: JSON.stringify({
          host: serverIP,
          username: username,
          port: port
        })
      });

      if (!response.ok) {
        throw new Error('Erreur serveur lors de la génération des commandes SSH');
      }

      const sshData = await response.json();
      console.log('📥 Commandes SSH reçues du serveur:', sshData);

      if (!sshData.success) {
        throw new Error(sshData.error || 'Erreur inconnue du serveur');
      }

      // EXÉCUTION CÔTÉ CLIENT avec les commandes du serveur
      const commands = sshData.commands;
      const urls = sshData.commands.urls;

      // TENTATIVE 1: Windows Terminal moderne avec URL
      try {
        console.log('🚀 Tentative Windows Terminal:', urls.windowsTerminal);
        window.location.href = urls.windowsTerminal;
        
        setTimeout(() => {
          setConnectionStatus('connected');
          setTimeout(() => setConnectionStatus('ready'), 2000);
        }, 1000);
        
        return;
      } catch (err) {
        console.log('Windows Terminal échoué:', err);
      }

      // TENTATIVE 2: PuTTY Protocol Handler avec login pré-intégré
      try {
        console.log('🔧 Tentative PuTTY:', urls.putty);
        if (hiddenLinkRef.current) {
          hiddenLinkRef.current.href = urls.putty;
          hiddenLinkRef.current.click();
        } else {
          window.location.href = urls.putty;
        }
        
        setTimeout(() => {
          setConnectionStatus('connected');
          setTimeout(() => setConnectionStatus('ready'), 2000);
        }, 500);
        
        return;
      } catch (err) {
        console.log('PuTTY protocol handler échoué:', err);
      }

      // TENTATIVE FINALE: Copie + Instructions avec commandes serveur
      await copyToClipboard(commands.ssh, 'Commande SSH générée par le serveur');
      
      setConnectionStatus('failed');
      
      alert(`🚀 SSH ULTIMATE - CLIENT/SERVEUR

✅ Commande générée par le serveur: ${commands.ssh}

⚡ CONNEXION ULTRA-RAPIDE:
1. Win+R
2. Taper: cmd
3. Entrée
4. Ctrl+V (commande avec user déjà inclus)
5. Entrée → Connexion directe !

🌍 Serveur: ${sshData.environment}
🕰️ Timestamp: ${sshData.timestamp}

💡 Alternatives disponibles:
• Windows Terminal: ${commands.windowsTerminal}
• PowerShell: ${commands.powershell}
• PuTTY: ${commands.putty}`);
      
      setTimeout(() => setConnectionStatus('ready'), 3000);

    } catch (error) {
      console.error('❌ Erreur SSH Ultimate:', error);
      setConnectionStatus('failed');
      
      // Fallback sur la méthode locale en cas d'échec serveur
      const localSSHCommand = generateSSHCommand();
      await copyToClipboard(localSSHCommand, 'Commande SSH locale');
      
      alert(`⚠️ FALLBACK - SSH LOCAL

📦 Serveur inaccessible: ${error.message}

✅ Commande locale copiée: ${localSSHCommand}

⚡ CONNEXION MANUELLE:
1. Win+R → cmd → Entrée
2. Ctrl+V → Entrée

🔧 Le serveur sera à nouveau disponible sous peu.`);
      
      setTimeout(() => setConnectionStatus('ready'), 3000);
    }
  };

  // Rendu du statut de connexion
  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connecting':
        return (
          <svg className="w-3 h-3 mr-1 animate-spin text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'connected':
        return (
          <svg className="w-3 h-3 mr-1 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-3 h-3 mr-1 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 002 2z" 
            />
          </svg>
        );
    }
  };

  const getButtonColor = () => {
    switch (connectionStatus) {
      case 'connecting':
        return 'bg-blue-600 hover:bg-blue-700 border-blue-500 animate-pulse';
      case 'connected':
        return 'bg-green-600 hover:bg-green-700 border-green-500';
      case 'failed':
        return 'bg-red-600 hover:bg-red-700 border-red-500';
      default:
        return 'bg-green-600 hover:bg-green-700 border-gray-300';
    }
  };

  const getButtonText = () => {
    switch (connectionStatus) {
      case 'connecting':
        return 'Connexion...';
      case 'connected':
        return 'Connecté !';
      case 'failed':
        return 'SSH';
      default:
        return 'SSH';
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Lien invisible pour protocol handler */}
      <a ref={hiddenLinkRef} href="#" style={{ display: 'none' }} />
      
      <div className="flex space-x-1">
        {/* Bouton principal - Connexion Ultimate ONE-CLICK */}
        <button
          onClick={launchSSHUltimate}
          disabled={connectionStatus === 'connecting'}
          className={`inline-flex items-center px-3 py-1 border shadow-sm text-xs leading-4 font-medium rounded-l-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 ${getButtonColor()}`}
          title={`ONE-CLICK SSH: ${options.username}@${server.name}:${options.port}`}
        >
          {getConnectionStatusIcon()}
          {getButtonText()}
        </button>

        {/* Bouton options avancées */}
        <button
          onClick={() => setShowOptions(!showOptions)}
          className="inline-flex items-center px-2 py-1 border-l-0 border border-gray-300 shadow-sm text-xs leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
          title="Méthodes et options SSH"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        {/* Bouton copie rapide */}
        <button
          onClick={() => copyToClipboard(generateSSHCommand())}
          className={`inline-flex items-center px-2 py-1 border-l-0 border shadow-sm text-xs leading-4 font-medium rounded-r-md focus:outline-none transition-all duration-300 ${
            copied 
              ? 'border-green-300 text-green-700 bg-green-50 scale-110' 
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
          title="Copier commande SSH"
        >
          {copied ? '✅' : '📋'}
        </button>
      </div>

      {/* Panel d'options avancées */}
      {showOptions && (
        <div className="absolute top-full right-0 mt-1 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-20 animate-in fade-in duration-200">
          <div className="p-4">
            <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
              🚀 SSH ONE-CLICK - {server.name}
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Ultimate</span>
            </h4>
            
            <div className="space-y-4">
              {/* Status et méthodes */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-3">
                <div className="text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <strong className="text-blue-800">Méthodes automatiques:</strong>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Multi-essais</span>
                  </div>
                  <div className="space-y-1 text-gray-700">
                    <div>1. 🖥️ Lancement direct via Node.js backend</div>
                    <div>2. 🪟 Windows Terminal moderne</div>
                    <div>3. 🔧 Protocol handler PuTTY</div>
                    <div>4. 📋 Copie + instructions express</div>
                  </div>
                </div>
              </div>

              {/* Paramètres temporaires */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Utilisateur:
                  </label>
                  <input
                    type="text"
                    value={options.username}
                    onChange={(e) => setOptions(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="root"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Port:
                  </label>
                  <input
                    type="text"
                    value={options.port}
                    onChange={(e) => setOptions(prev => ({ ...prev, port: e.target.value }))}
                    className="w-full text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="22"
                  />
                </div>
              </div>
              
              {/* Commandes générées */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs space-y-2">
                  <div>
                    <strong className="text-gray-800">SSH standard:</strong>
                    <code className="block bg-white p-2 rounded mt-1 break-all border text-green-700 font-mono">
                      {generateSSHCommand()}
                    </code>
                  </div>
                  <div>
                    <strong className="text-gray-800">Windows Terminal:</strong>
                    <code className="block bg-white p-2 rounded mt-1 break-all border text-blue-700 font-mono">
                      wt {generateSSHCommand()}
                    </code>
                  </div>
                </div>
              </div>
              
              {/* Actions rapides */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700 border-b pb-1">Actions manuelles:</p>
                
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      const wtUrl = `ms-terminal://cmd/k ${generateSSHCommand()}`;
                      window.location.href = wtUrl;
                    }}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2 rounded-md text-xs font-medium transition-colors duration-200 flex items-center justify-center"
                  >
                    🪟 Windows Terminal
                  </button>
                  
                  <button
                    onClick={() => {
                      const puttyUrl = generatePuttyUrl();
                      window.location.href = puttyUrl;
                    }}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-800 px-3 py-2 rounded-md text-xs font-medium transition-colors duration-200 flex items-center justify-center"
                  >
                    🔧 PuTTY Handler
                  </button>
                </div>
                
                <button
                  onClick={() => copyToClipboard(generateSSHCommand())}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-xs font-medium transition-colors duration-200"
                >
                  📋 Copier commande SSH
                </button>
              </div>

              {/* Guide d'installation */}
              <div className="border-t pt-3 text-xs text-gray-600 bg-yellow-50 rounded p-2">
                <strong className="text-yellow-800">🔧 Configuration one-click:</strong>
                <div className="mt-2 space-y-1">
                  <div>1. Exécuter <code className="bg-yellow-100 px-1 rounded">install-putty-final.bat</code></div>
                  <div>2. Redémarrer le navigateur</div>
                  <div>3. Clic = connexion instantanée ! ⚡</div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowOptions(false)}
              className="mt-4 w-full text-xs text-gray-500 hover:text-gray-700 py-2 border-t"
            >
              Fermer les options
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PuttyButtonUltimate;